import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
} from "@mui/material";
import { ProducerCommissionCompanyOverride } from "../../interfaces/DTO/commissions/producerCommission";
import { formatHandlerCode, formatCurrency } from "../../utils/namesFormater";
import { colorHandlerCode } from "../../utils/colorEnumFormatter";

interface Props {
  overrides: ProducerCommissionCompanyOverride[];
  initialAmount?: number;
}

interface BucketData {
  handler_code: string;
  saving_fee: number;
  max_value: number;
  bucket: ProducerCommissionCompanyOverride;
}

const CommissionComparisonChart: React.FC<Props> = ({ overrides, initialAmount = 300000 }) => {
  const [amountInput, setAmountInput] = useState<string>(initialAmount.toString());
  const [amount, setAmount] = useState<number>(initialAmount);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const parsed = Number(amountInput);
      if (!isNaN(parsed)) setAmount(parsed);
    }, 400);
    return () => clearTimeout(timeout);
  }, [amountInput]);

  const chartData = useMemo(() => {
    const grouped: Record<string, ProducerCommissionCompanyOverride[]> = {};
    for (const row of overrides) {
      if (!grouped[row.handler_code]) grouped[row.handler_code] = [];
      grouped[row.handler_code].push(row);
    }

    const buckets: BucketData[] = Object.entries(grouped)
      .map(([handler_code, rows]) => {
        const sorted = [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        const bucket = sorted.find(
          (r) =>
            (r.min_value ?? 0) <= amount &&
            (r.max_value ?? Infinity) >= amount
        );
        if (!bucket) return null;
        return {
          handler_code,
          saving_fee: bucket.saving_fee || 0,
          max_value: bucket.max_value || amount,
          bucket,
        };
      })
      .filter(Boolean) as BucketData[];

    const sortedBuckets = buckets.sort((a, b) => {
      if (a.saving_fee === b.saving_fee) {
        return a.max_value - b.max_value;
      }
      return a.saving_fee - b.saving_fee;
    });

    const virtual: BucketData = {
      handler_code: '__virtual',
      saving_fee: 0,
      max_value: amount,
      bucket: {
        range_key: '',
        handler_code: '',
        product_type: 0,
        user_id: 0,
        sort_order: 0,
        official: false,
        is_shared: false,
      },
    };

    return [virtual, ...sortedBuckets];
  }, [overrides, amount]);

  const globalMax = useMemo(() => {
    return Math.max(...chartData.map(({ max_value }) => max_value));
  }, [chartData]);

  return (
    <Box sx={{ mb:3}}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Typography variant="caption" color="textSecondary">
          {formatCurrency(globalMax)}
        </Typography>
      </Box>
      <Typography variant="h6" mb={2}>השוואת דמי ניהול עבור סכום</Typography>
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <TextField 
          type="number"
          label="סכום"
          
          value={amountInput}
          onChange={(e) => {
              const val = e.target.value;
              setAmountInput(val);
              const parsed = Number(val);
              if (!isNaN(parsed)) setAmount(parsed);
            }}
          sx={{ width: 200 }}
        />
        <Box sx={{ minWidth: 200 }}>
          <input
            type="range"
            min={0}
            max={2000000}
            value={amount}
            onChange={(e) => setAmountInput(e.target.value)}
            style={{ width: '100%' }}
            
          />
        </Box>     
      </Box>

      <Stack spacing={2}>
                {chartData.map(({ handler_code, saving_fee, max_value }) => {
          const relativePercent = (max_value / globalMax) * 100;
          const valuePercent = Math.min((amount / globalMax) * 100, 100);
                    const triangleLeft = `${valuePercent}%`;
          return (
            <Stack key={handler_code} direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 80 }}>
                {handler_code !== '__virtual' && (
                  <Typography variant="body2">{formatHandlerCode(handler_code)}</Typography>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box position="relative" height={20}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                       backgroundColor: handler_code === '__virtual' ? 'transparent' : '#eee',
                      borderRadius: 1,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: `${relativePercent}%`,
                        maxWidth: "100%",
                        height: "100%",
                        backgroundColor: handler_code === '__virtual' ? 'transparent' : colorHandlerCode(handler_code),
                        borderLeft: handler_code === '__virtual' ? '5px solid red' : 'none',
                      }}
                    />
                    
                {handler_code !== '__virtual' && (
                    <Typography variant="caption" sx={{ position: "absolute", top: 2, left: 4, color: "black" }}>
                        {formatCurrency(max_value)}
                    </Typography>
                    )}
                    {handler_code !== '__virtual' && (
                    <Typography variant="caption" sx={{ position: "absolute", top: 2, right: 8, color: "black" }}>
                        {saving_fee.toFixed(2)}%
                    </Typography>
                    )}
                    <Box
                      sx={{
                        position: "absolute",
                        top: -8,
                        left: triangleLeft,
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "5px solid transparent",
                        borderRight: "5px solid transparent",
                        borderBottom: "8px solid red",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default CommissionComparisonChart;
